/**
 * Renderer Registry — 渲染器动态注册表 (Architecture 3.0 Phase 4)
 *
 * 设计思想：
 * - 每种文件格式通过 registerRenderer 注册为一个"插槽"
 * - FilePreviewModal 不再硬编码任何 if-else 格式判断
 * - 新增格式只需在此处或业务模块中追加一行 registerRenderer 即可
 *
 * 统一渲染器接口：
 * - RendererProps 会同时携带 blob + url + name
 * - 各渲染器只使用自己需要的字段
 */

import React from 'react';

// ─── 统一渲染器 Props ──────────────────────
export interface RendererProps {
  /** 文件的 Blob 对象，用于二进制格式渲染器 (Word/Excel/PPT/Text) */
  blob: Blob;
  /** 文件的 ObjectURL，用于 URL-based 渲染器 (PDF/Image/Audio/Video) */
  url: string;
  /** 文件名（含扩展名） */
  name: string;
}

// ─── 注册表条目 ────────────────────────────
export interface RendererEntry {
  /** 支持的文件扩展名列表（小写） */
  extensions: string[];
  /** 加载时显示的标签，如 "PDF 引擎" */
  label: string;
  /** 懒加载工厂函数 — 返回一个 Promise<{ default: Component }> */
  load: () => Promise<{ default: React.FC<RendererProps> }>;
}

// ─── 全局注册表 (Module-level Singleton) ───
const registry: RendererEntry[] = [];

/**
 * 注册一个渲染器到全局注册表
 */
export function registerRenderer(entry: RendererEntry): void {
  registry.push(entry);
}

/**
 * 根据文件扩展名查找匹配的渲染器
 * @returns 匹配的 RendererEntry，未找到返回 null
 */
export function getRenderer(ext: string): RendererEntry | null {
  const lower = ext.toLowerCase();
  return registry.find(e => e.extensions.includes(lower)) ?? null;
}

/**
 * 获取所有已注册的渲染器（调试用）
 */
export function getAllRenderers(): readonly RendererEntry[] {
  return registry;
}

// ─── 内置渲染器注册 ────────────────────────
// 重型渲染器 — 各自在独立 chunk 中按需加载
// 注：既有渲染器的 props 签名各异 (PdfRenderer: {url}, DocxRenderer: {blob})
//     通过适配器包装为统一的 RendererProps 接口，避免改动已验证的渲染器代码

registerRenderer({
  extensions: ['pdf'],
  label: 'PDF 引擎',
  load: () => import('../components/preview-modal/PdfRenderer').then(mod => ({
    default: ({ url }: RendererProps) => React.createElement(mod.default, { url }),
  })),
});

registerRenderer({
  extensions: ['docx', 'doc'],
  label: 'Word 引擎',
  load: () => import('../components/preview-modal/DocxRenderer').then(mod => ({
    default: ({ blob }: RendererProps) => React.createElement(mod.default, { blob }),
  })),
});

registerRenderer({
  extensions: ['xlsx', 'xls', 'csv'],
  label: 'Excel 引擎',
  load: () => import('../components/preview-modal/ExcelRenderer').then(mod => ({
    default: ({ blob }: RendererProps) => React.createElement(mod.default, { blob }),
  })),
});

registerRenderer({
  extensions: ['pptx'],
  label: 'PPT 引擎',
  load: () => import('../components/preview-modal/PptxRenderer').then(mod => ({
    default: ({ blob }: RendererProps) => React.createElement(mod.default, { blob }),
  })),
});

// 轻量渲染器 — 内联无第三方依赖

registerRenderer({
  extensions: ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'ico'],
  label: '图片',
  load: () => import('../components/preview-modal/ImageRenderer'),
});

registerRenderer({
  extensions: ['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a', 'wma'],
  label: '音频',
  load: () => import('../components/preview-modal/AudioRenderer'),
});

registerRenderer({
  extensions: ['mp4', 'webm', 'mov', 'avi', 'mkv'],
  label: '视频',
  load: () => import('../components/preview-modal/VideoRenderer'),
});

registerRenderer({
  extensions: ['txt', 'json', 'md', 'log', 'xml', 'html', 'css', 'js', 'ts', 'py', 'java', 'c', 'cpp', 'yaml', 'yml', 'toml', 'ini', 'env'],
  label: '文本',
  load: () => import('../components/preview-modal/TextRenderer'),
});
